import { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { HopChartsProvider } from 'hopcharts';
import { marketProvider, queryClient } from './lib/market';
import { applyTheme, useSettings } from './state/settings';
import { Toaster } from './components/ui/Toast';
import { useAlertEngine } from './features/alerts/alertsStore';
import { RouteError } from './pages/RouteError';

const LandingPage = lazy(() => import('./pages/LandingPage').then((m) => ({ default: m.LandingPage })));
const TerminalPage = lazy(() => import('./features/terminal/TerminalPage').then((m) => ({ default: m.TerminalPage })));
const MarketsPage = lazy(() => import('./pages/MarketsPage').then((m) => ({ default: m.MarketsPage })));
const AgentPage = lazy(() => import('./features/agent/AgentPage').then((m) => ({ default: m.AgentPage })));
const WidgetsPage = lazy(() => import('./features/widgets/WidgetsPage').then((m) => ({ default: m.WidgetsPage })));
const EmbedPage = lazy(() => import('./features/widgets/EmbedPage').then((m) => ({ default: m.EmbedPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));

function PageFallback() {
  return <div className="h-dvh bg-ink" aria-busy="true" />;
}

/** Shared providers for the site: market data, query cache, theme, alerts, toasts. */
function Root() {
  const theme = useSettings((s) => s.theme);
  useEffect(() => applyTheme(theme), [theme]);
  useAlertEngine();
  return (
    <>
      <Suspense fallback={<PageFallback />}>
        <Outlet />
      </Suspense>
      <Toaster />
    </>
  );
}

/** Widgets in iframes: no alert engine, theme comes from the URL. */
function EmbedRoot() {
  return (
    <Suspense fallback={<div className="h-dvh" aria-busy="true" />}>
      <Outlet />
    </Suspense>
  );
}

const router = createBrowserRouter([
  {
    path: '/embed',
    element: <EmbedRoot />,
    errorElement: <RouteError />,
    children: [{ path: ':widget', element: <EmbedPage /> }],
  },
  {
    element: <Root />,
    errorElement: <RouteError />,
    children: [
      { path: '/', element: <LandingPage /> },
      { path: '/chart', element: <TerminalPage /> },
      { path: '/chart/:symbol', element: <TerminalPage /> },
      { path: '/markets', element: <MarketsPage /> },
      { path: '/ai', element: <AgentPage /> },
      { path: '/widgets', element: <WidgetsPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <HopChartsProvider provider={marketProvider} queryClient={queryClient}>
        <RouterProvider router={router} />
      </HopChartsProvider>
    </QueryClientProvider>
  );
}
